import { useState, useEffect, useMemo, useRef } from "react";
import { createUseStyles } from "react-jss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Sidebar, {
  SIDEBAR_EXPANDED,
  SIDEBAR_WIDTH,
} from "../components/refresh/tours/Sidebar";
import Content from "../components/refresh/tours/Content";
import Tabs from "../components/refresh/tours/Tabs";
import { fetchApi } from "../services/api";
import TopBar from "../components/refresh/TopBar";

const useStyles = createUseStyles({
  background: {
    backgroundColor: "#252525",
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    height: "100vh",
    width: "100%",
  },
  sidebar: {
    height: "100%",
    width: SIDEBAR_WIDTH,
    overflowX: "visible",

    "@media (max-width: 800px)": {
      width: SIDEBAR_EXPANDED,
    },
  },
  content: {
    backgroundColor: "#3A3A3A",
    flexGrow: 1,
    borderRadius: [8, 0, 0, 0],
    width: 0,
    height: "100%",
  },

  image: {
    maxHeight: 40,
    maxWidth: 40,
    borderRadius: 3,
    margin: 15,
  },
  svg: {
    color: "#f90",
    margin: 15,
    width: 40,
    height: 40,
    borderRadius: 3,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",

    "& svg": {
      height: "100%",
    },
  },
  search: {
    extend: "svg",
    color: "white",
  },
});

const useTours = (team, search, searching) => {
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (team) {
      if (searching && search.length <= 2) {
        setTours([]);
        return;
      }
      const controller = new AbortController();
      setLoading(true);
      let attempts = 0;
      const maxAttempts = 3;
      const makeRequest = async () => {
        try {
          const res = await fetchApi(
            searching
              ? `/tour/list?q=${search}&limit=false`
              : `/team/${team}/tour/list?limit=false`,
            {
              version: "v2",
              signal: controller.signal,
            }
          );
          setLoading(false);
          setTours(
            res.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
          );
        } catch (err) {
          attempts++;
          if (attempts < maxAttempts) {
            await makeRequest();
          } else {
            setLoading(false);
            console.error(err);
          }
        }
      };
      makeRequest();
      return () => {
        controller.abort();
        setLoading(false);
      };
    }
  }, [team, search, searching]);

  return [tours, loading];
};

const useAccounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    fetchApi("/accounts/list", { version: "v1" })
      .then(res => {
        setLoading(false);
        setAccounts(res);
      })
      .catch(err => {
        setLoading(false);
        console.error(err);
      });
  }, []);

  return [accounts, loading];
};

const Home = () => {
  const [search, setSearch] = useState("");
  const [searchText, setSearchText] = useState("");
  const debounce = useRef();
  const [focused, setFocused] = useState(false);
  const [searching, setSearching] = useState(false);
  const [accounts] = useAccounts();
  const [activeAccount, setActiveAccount] = useState(null);
  const [activeTeam, setActiveTeam] = useState(null);
  const [searchTab, setSearchTab] = useState("Tours");
  const [tours, loading] = useTours(activeTeam?.id, search, searching);
  const classes = useStyles();

  const clearSearch = () => {
    setSearch("");
    setSearchText("");
    setSearchTab("Tours");
    setSearching(false);
  };

  useEffect(() => {
    if (focused) setSearching(true);
  }, [focused]);

  useEffect(() => {
    debounce.current = setTimeout(() => setSearch(searchText), 500);
    return () => clearTimeout(debounce.current);
  }, [searchText]);

  const groupedTours = useMemo(() => {
    let grouped = {};
    for (const tour of tours) {
      const account = accounts.find(acc => acc.id === tour.account);
      if (account) {
        grouped[account.id] ??= {
          ...account,
          tours: [],
        };
        grouped[account.id].tours.push(tour);
      }
    }
    return Object.values(grouped);
  }, [accounts, tours]);

  return (
    <div className={classes.background}>
      <TopBar />
      <div className={classes.sidebar}>
        <Sidebar
          searching={searching}
          setFocused={setFocused}
          accounts={accounts}
          search={searchText}
          setSearch={setSearchText}
          clearSearch={clearSearch}
          active={activeAccount?.token}
          setActive={setActiveAccount}
          activeTeam={activeTeam}
          setActiveTeam={setActiveTeam}
        />
      </div>
      <div className={classes.content}>
        <Content
          loading={loading}
          account={activeAccount?.id}
          team={activeTeam?.id}
          searching={searching}
          tours={searching ? groupedTours : tours}
          searchTab={searchTab}
          accounts={accounts.filter(acc =>
            (acc.name || '')
              .toLocaleUpperCase()
              .includes(searchText.toLocaleUpperCase()),
          )}
          clearSearch={clearSearch}
          setActiveAccount={setActiveAccount}
          icon={
            searching ? (
              <div className={classes.search}>
                <FontAwesomeIcon icon={["fas", "search"]} fixedWidth />
              </div>
            ) : activeAccount && activeAccount.logo ? (
              <img
                className={classes.image}
                src={activeAccount.logo}
                alt={activeAccount.name || 'Account'}
              />
            ) : (
              <div className={classes.svg}>
                <FontAwesomeIcon icon={["fal", "building"]} fixedWidth />
              </div>
            )
          }
          title={
            activeAccount
              ? searching
                ? `Search results: "${searchText}"`
                : (activeAccount.name || 'Unnamed Account')
              : "Loading..."
          }
        >
          <Tabs
            team={activeTeam?.name || 'Unnamed Team'}
            setActive={setSearchTab}
            searching={searching}
            tab={searchTab}
          />
        </Content>
      </div>
    </div>
  );
};

export default Home;
